import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export type ListFunnelSegmentState = "hit" | "skipped" | "pending";

export type ListFunnelProgress = {
    /** Events (or stages) with customer data — numerator in the ring center. */
    filledCount: number;
    /** Total events (or stages) — denominator in the ring center. */
    total: number;
    segmentStates: readonly ListFunnelSegmentState[];
    /** Overrides default `filledCount/total` center label when provided. */
    label?: string;
};

/** Parses journey event count column values; non-numeric or empty counts as zero. */
export function parseListFunnelEventCount(raw: unknown): number {
    if (raw === null || raw === undefined) {
        return 0;
    }
    if (typeof raw === "number") {
        return Number.isFinite(raw) && raw > 0 ? raw : 0;
    }
    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (trimmed === "") {
            return 0;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }
    return 0;
}

/**
 * Resolves per-stage funnel segment states from ordered stage count columns.
 * - hit: count > 0 (green)
 * - skipped: count is 0 but a later stage has count > 0 (red)
 * - pending: no count yet and not skipped (grey)
 */
export function resolveListFunnelProgress(
    additionalColumnValues: Record<Uuid, unknown>,
    stageColumnIds: readonly Uuid[],
): ListFunnelProgress {
    const total = stageColumnIds.length;
    if (total === 0) {
        return {filledCount: 0, total: 0, segmentStates: []};
    }

    const stageCounts = stageColumnIds.map((columnId) =>
        parseListFunnelEventCount(additionalColumnValues[columnId]),
    );

    let highestActiveIndex = -1;
    for (let index = total - 1; index >= 0; index -= 1) {
        const stageCount = stageCounts[index] ?? 0;
        if (stageCount > 0) {
            highestActiveIndex = index;
            break;
        }
    }

    const segmentStates: ListFunnelSegmentState[] = stageCounts.map((count, index) => {
        if (count > 0) {
            return "hit";
        }
        if (highestActiveIndex < 0) {
            return "pending";
        }
        if (index < highestActiveIndex) {
            return "skipped";
        }
        return "pending";
    });

    const filledCount = stageCounts.filter((count) => count > 0).length;

    return {
        filledCount,
        total,
        segmentStates,
    };
}
